/**
 * Останній горизонт, на якому людина була.
 *
 * Потрібен тому, що сторінки (`settings`, `search`, `feed`, `inbox`) до
 * жодного горизонту не належать, а повертатися з них кудись треба.
 * Заміряно 2026-09-11: з будь-якої сторінки не було шляху назад у календар
 * ні на ПК, ні на телефоні — єдиним виходом лишалася кнопка «назад»
 * у браузері.
 *
 * **Назад туди, де був, а не «на сьогодні».** Різниця помітна рівно тоді,
 * коли з грудневого тижня пішли в налаштування: «сьогодні» викинуло б
 * людину з того місця, яке вона читала.
 *
 * `sessionStorage`, а не `localStorage`: памʼять тут доречна рівно доти,
 * доки триває сеанс роботи. Учорашній тиждень наступного ранку — це вже
 * не «де я був», це минуле, і повертати туди означало б відкривати
 * застосунок не на сьогодні.
 *
 * Зберігається **ключ маршруту**, а не розібраний обʼєкт, і читається він
 * назад тим самим `parseRoute`, яким застосунок читає адресний рядок.
 * Другий парсер розійшовся б із першим мовчки.
 */
import { type Horizon, today } from "@beresta/core";
import { parseRoute } from "../router/index.js";

const KEY = "beresta:last-horizon";

/** Куди повертає «назад», коли памʼяті ще немає: сьогоднішній день. */
function fallback(): Horizon {
  return { kind: "day", key: today().toString() };
}

export function rememberHorizon(horizon: Horizon): void {
  try {
    sessionStorage.setItem(KEY, horizon.key);
  } catch {
    // Приватне вікно: памʼяті не буде, «назад» поведе на сьогодні.
    // Застосунок через це не має не відкритися.
  }
}

/**
 * Горизонт, на який веде «назад». Ключ із памʼяті проходить той самий
 * розбір, що й адреса: зіпсований або застарілий формат просто не
 * впізнається, і людина потрапляє на сьогодні замість екрана з помилкою.
 */
export function lastHorizon(): Horizon {
  let saved: string | null = null;
  try {
    saved = sessionStorage.getItem(KEY);
  } catch {
    return fallback();
  }
  if (!saved) return fallback();

  const route = parseRoute(`/${saved}`);
  return route?.kind === "horizon" ? route.horizon : fallback();
}
